<script>
  import { getMyBinders } from '../../lib/services/recent-binders.js';
  import { getBinderColor } from '../../lib/logic/utils.js';
  import { createTrade } from '../../lib/services/trade-service.js';
  import { binderState } from '../../lib/stores/binder.svelte.js';
  import { annotateClaimedBinders, miniPillLabel } from '../../lib/logic/want-overlap-logic.js';

  // `overlapBinders`: the Want Overlap's per-binder counts when opened from
  // the pill (spec 2026-09-21); empty otherwise, so no mini pills render.
  let {
    isOpen = false,
    currentBinderUrl = '',
    overlapBinders = [],
    onClose,
    onTradeCreated,
  } = $props();

  /** @type {Array<{ url: string, name?: string, owner?: string, overlapCount: number }>} */
  let binders = $state([]);
  let selectedBinder = $state(null);
  let isCreating = $state(false);
  let error = $state(null);

  // Load claimed binders when modal opens
  $effect(() => {
    if (isOpen) {
      selectedBinder = null;
      isCreating = false;
      error = null;
      loadBinders();
    }
  });

  // A trade is proposed FROM a binder you hold: only claimed binders (My
  // Binders) are offered, never recents (VISUALS.md "Propose Trade Modal").
  async function loadBinders() {
    try {
      const claimed = await getMyBinders();
      binders = annotateClaimedBinders(claimed, overlapBinders, currentBinderUrl);
    } catch (err) {
      console.error('[ProposeTradeModal] Error loading claimed binders:', err);
      binders = [];
    }
  }

  function selectBinder(binder) {
    selectedBinder = binder;
  }

  async function handleConfirm() {
    if (!selectedBinder || isCreating) return;
    isCreating = true;
    error = null;

    try {
      // Get binder info for both sides
      const binderAInfo = {
        name: selectedBinder.name,
        owner: selectedBinder.owner,
      };
      const binderBInfo = {
        name: binderState.binderName,
        owner: binderState.binderOwner,
      };

      // Side A = the binder being proposed TO (selectedBinder)
      // Side B = the current binder (user's binder)
      const selectedPath = new URL(selectedBinder.url, window.location.origin).pathname;
      const tradeId = await createTrade(
        selectedPath,
        currentBinderUrl,
        [],
        binderAInfo,
        binderBInfo
      );

      // Copy trade URL to clipboard
      const tradeUrl = `${window.location.origin}/trades/${tradeId}`;
      try {
        await navigator.clipboard.writeText(tradeUrl);
        console.log('[ProposeTradeModal] Trade URL copied to clipboard:', tradeUrl);
      } catch (clipErr) {
        console.warn('[ProposeTradeModal] Could not copy trade URL:', clipErr);
      }

      onTradeCreated?.(tradeId);
      onClose?.();
    } catch (err) {
      console.error('[ProposeTradeModal] Error creating trade:', err);
      error = 'Failed to create trade. Please try again.';
      isCreating = false;
    }
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      onClose?.();
    }
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="modal-overlay" onclick={handleOverlayClick} onkeydown={handleKeydown}>
    <div class="modal-content">
      <h2>Propose a Trade</h2>
      <p class="modal-subtitle">Select a binder to trade with</p>

      {#if error}
        <p class="modal-error">{error}</p>
      {/if}

      <div class="binder-list">
        {#if binders.length === 0}
          <p class="empty-message">
            Trades start from a binder you've claimed. Drag one of yours into My Binders in the
            sidebar.
          </p>
        {:else}
          {#each binders as binder (binder.url)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="binder-option"
              class:selected={selectedBinder === binder}
              onclick={() => selectBinder(binder)}
            >
              <div
                class="binder-option-color"
                style:background-color={getBinderColor(binder.name)}
              ></div>
              <div class="binder-option-info">
                <span class="binder-option-name">{binder.name || 'Unnamed Binder'}</span>
                {#if binder.owner}
                  <span class="binder-option-owner">{binder.owner}</span>
                {/if}
              </div>
              {#if binder.overlapCount > 0}
                <span class="mini-pill" title="Cards they want in this binder"
                  >{miniPillLabel(binder.overlapCount)}</span
                >
              {/if}
              {#if selectedBinder === binder}
                <svg
                  class="check-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              {/if}
            </div>
          {/each}
        {/if}
      </div>

      <div class="modal-buttons">
        <button
          class="btn-confirm"
          disabled={!selectedBinder || isCreating}
          onclick={handleConfirm}
        >
          {isCreating ? 'Creating...' : 'Start Trade'}
        </button>
        <button class="btn-cancel" onclick={onClose} disabled={isCreating}> Cancel </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 1rem;
  }

  .modal-content {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    max-width: 400px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15);
  }

  .modal-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.25rem 0;
  }

  .modal-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem 0;
  }

  .modal-error {
    font-size: 0.875rem;
    color: #dc2626;
    margin: 0 0 0.75rem 0;
    padding: 0.5rem;
    background: #fef2f2;
    border-radius: 8px;
  }

  .binder-list {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 1rem;
    max-height: 300px;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .empty-message {
    color: #9ca3af;
    font-size: 0.875rem;
    text-align: center;
    padding: 2rem 1rem;
  }

  .binder-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      background-color 0.15s;
  }

  .binder-option:hover {
    border-color: #d1d5db;
    background-color: #f9fafb;
  }

  .binder-option.selected {
    border-color: #667eea;
    background-color: #f0f0ff;
  }

  .binder-option-color {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .binder-option-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .binder-option-name {
    font-weight: 500;
    font-size: 0.9rem;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .binder-option-owner {
    font-size: 0.8rem;
    color: #6b7280;
  }

  /* A small version of the Want Overlap pill (VISUALS.md "Propose Trade
     Modal"): same gradient, same shape, per-binder count. */
  .mini-pill {
    flex: 0 0 auto;
    margin-left: auto;
    margin-right: 0.5rem;
    padding: 0.15rem 0.6rem;
    border-radius: 9999px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .check-icon {
    width: 20px;
    height: 20px;
    color: #667eea;
    flex-shrink: 0;
  }

  .modal-buttons {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .btn-confirm {
    width: 100%;
    padding: 0.875rem;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    transition:
      transform 0.1s,
      opacity 0.15s;
  }

  .btn-confirm:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .btn-confirm:active:not(:disabled) {
    transform: scale(0.98);
  }

  .btn-confirm:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .btn-cancel {
    width: 100%;
    padding: 0.75rem;
    border: none;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    background: #f3f4f6;
    color: #4b5563;
    transition: background-color 0.15s;
  }

  .btn-cancel:hover:not(:disabled) {
    background: #e5e7eb;
  }

  .btn-cancel:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
